import { gql } from '@apollo/client'
import { useApolloClient } from '@nobia/zeus-components/lib/apollo'
import { Icon as IconUI } from '@nobia/zeus-components/lib/icons'
import LoadingIndicator from '@nobia/zeus-components/lib/loadingIndicator'
import styled, { rem, theme } from '@nobia/zeus-components/lib/styled'
import { Link } from '@nobia/zeus-components/lib/text'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { RetrieveAddressFragment } from '../../graphql/PostcodeSearchFragment'
import { margins } from '../../styles/margins'
import { useTranslation } from '../../utils/useTranslation'
import { findAddress, findSpecificAddress } from './findAddress'
import useDebounce from './useDebounce'

const MainContainer = styled.div`
  margin-top: ${rem(6)};
`
const InputContainer = styled.div`
  width: 100%;
  position: relative;
`

const Overlap = styled.div`
  display: grid;
`
const Layer = styled.div`
  grid-column: 1;
  grid-row: 1;
  z-index: ${(p) => p.zIndex};
  margin: ${(p) => p.margin};
`
const Input = styled.input`
  background-color: #fff;
  width: 100%;
  height: ${theme('forms.height')};
  padding: 0 ${theme('forms.padding')};
  border-radius: ${(p) => rem(p.theme.forms.borderRadius)};
  border: ${rem(1)} solid ${theme('forms.colors.border')};
  font-weight: ${theme('forms.fontWeight')};
  font-size: ${theme('forms.fontSize')};
  font-family: ${theme('forms.fontFamily')};
  margin: ${rem(3)} 0 ${rem(14)};
  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus,
  :-webkit-autofill:active {
    box-shadow: 0 0 0 30px #fff inset !important;
  }
`
const ResultsContainer = styled.div`
  position: absolute;

  top: ${(p) => (p.above ? null : '100%')};
  bottom: ${(p) => (p.above ? '100%' : null)};

  border: 1px solid #cbd0d4;
  border-radius: 6px;
  max-height: 250px;
  overflow: scroll;
  z-index: 100;
  background-color: #fff;
  min-width: 100%;
  ${(props) => props.theme.media.min.sm`
    max-height: 400px;
    overflow-y: scroll;
    overflow-x: hidden;
  `}
`
const Flex = styled.div`
  display: flex;
  align-items: center;
`
const Address = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 10px 10px;
  cursor: pointer;
  :nth-child(even) {
    background-color: #f3f4f5;
  }
`
const AddressText = styled.span`
  padding-left: 5px;
`
const Icon = styled(IconUI)`
  position: relative;
  top: -1px;
`
const Wrapper = styled.div`
  border-radius: ${rem(6)};
  background-color: #f3f4f5;
  padding: ${rem(margins.s)};
`
const Postcode = styled.div`
  font-size: ${rem(25)};
`
const SelectedAddress = styled.div`
  ${(p) => p.theme.media.min.sm`
    display: flex;
  `}
`
const Text = styled.div`
  padding-left: 0;
  max-width: ${rem(270)};
  display: inline;
  ${(p) => p.theme.media.min.sm`
    padding-left: ${rem(5)};
    display:block;
  `}
`
const ChangeLocation = styled(Link)`
  font-size: ${rem(16)};
  font-weight: 500;
  cursor: pointer;
  position: relative;
  top: 5px;
  margin-left: auto;
  display: block;
  ${(p) => p.theme.media.min.sm`
    display:inline;
    top: -30px;
  `}
`
const Desktop = styled.div`
  display: none;
  ${(p) => p.theme.media.min.sm`
    display:block;
  `}
`

const Description = styled.span`
  text-align: right;
  opacity: 0.6;
`

const RetrieveAddressQuery = gql`
  ${RetrieveAddressFragment}
  query RetrieveAddressQuery($id: String!) {
    retrieveAddress(id: $id) {
      ...RetrieveAddressItem
    }
  }
`

const PostalCodeSelector = ({
  onAddressChange,
  address,
  postalCode,
  boundaryRef,
}) => {
  const { t } = useTranslation('app.components.address')
  const client = useApolloClient()
  const [addressesPos, setAddressesPos] = useState(false)
  const [addresses, setAddresses] = useState(null)
  const [postalCodeInput, setPostalCodeInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [valid, setValid] = useState(null)
  const [edit, setEdit] = useState(false)
  const postcodeInputRef = useRef()
  const addressesRef = useRef()
  const { debouncer, debouncedValue } = useDebounce('', 500)

  useEffect(() => {
    setPostalCodeInput(postalCode)
    setEdit(!postalCode)
  }, [postalCode])

  useEffect(() => {
    setAddresses(null)
  }, [edit])

  useEffect(() => {
    if (!debouncedValue) {
      setAddresses(null)
      return
    }
    setLoading(true)
    findAddress(client, debouncedValue).then((results) => {
      if (!results || results.length === 0) return
      setAddresses(results)
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  const selectGenericAddress = (addressId) => {
    setLoading(true)
    findSpecificAddress(client, addressId).then((results) => {
      setAddresses(results)
      setLoading(false)
    })
  }
  const selectSpecificAddress = (addressId) => {
    client
      .query({
        query: RetrieveAddressQuery,
        variables: { id: addressId },
      })
      .then(({ data: { retrieveAddress } }) => {
        onAddressChange({
          ...retrieveAddress,
        })
        setValid(null)
        setEdit(false)
      })
  }

  const selectAddress = (id) => {
    const isSplit = id?.split('|')
    let isSpecific = false

    const has5Entries = isSplit?.length === 5
    const hasA = isSplit[isSplit?.length - 2] === 'A'
    const hasB = isSplit[isSplit?.length - 2] === 'B'
    if (has5Entries || hasA || hasB) {
      isSpecific = true
    }

    if (isSpecific) {
      selectSpecificAddress(id)
      return
    }
    selectGenericAddress(id)
  }

  const addressString = ({ addressLine1, addressLine2, addressLine3 }) =>
    [addressLine1, addressLine2, addressLine3].filter((a) => a).join(', ')

  useEffect(() => {
    if (
      !boundaryRef?.current ||
      !addressesRef?.current ||
      !postcodeInputRef?.current ||
      !addresses
    ) {
      return
    }

    const boundaryRect = boundaryRef.current.getBoundingClientRect()
    const addressRect = addressesRef.current.getBoundingClientRect()
    const postcodeInputRect = postcodeInputRef.current.getBoundingClientRect()
    const maxBottom =
      postcodeInputRect.bottom +
      postcodeInputRect.height +
      addressRect.height +
      20
    setAddressesPos(maxBottom > boundaryRect.bottom)
  }, [boundaryRef, addressesRef, addresses, postcodeInputRef])

  return (
    <MainContainer>
      {edit ? (
        <>
          <InputContainer>
            <Overlap>
              <Layer zIndex="0">
                <Input
                  ref={postcodeInputRef}
                  placeholder={t('placeholder')}
                  data-cs-mask
                  name="postalcode"
                  autocomplete="nope"
                  type="text"
                  value={postalCodeInput}
                  valid={valid}
                  onChange={(e) => {
                    setPostalCodeInput(e.target.value)
                    debouncer(e.target.value)
                    setValid(null)
                  }}
                />
              </Layer>
              {loading && (
                <Layer zIndex="1" margin="1.2rem 0 0">
                  <LoadingIndicator type="bouncy" size="small" />
                </Layer>
              )}
            </Overlap>
            {addresses && (
              <ResultsContainer above={addressesPos} ref={addressesRef}>
                {addresses.map(({ id, text, description }) => (
                  <Address key={id} onClick={() => selectAddress(id)}>
                    <Flex>
                      <Icon type="location" width={13} height={13} />
                      <AddressText>{text}</AddressText>
                    </Flex>
                    <Description>{description}</Description>
                  </Address>
                ))}
              </ResultsContainer>
            )}
          </InputContainer>
        </>
      ) : (
        <Wrapper>
          <Postcode data-cs-mask>{postalCode}</Postcode>
          <SelectedAddress>
            <Desktop>
              <Icon type="location" width={13} height={13} />
            </Desktop>
            <div>
              <Text data-cs-mask>{addressString(address)}, </Text>
              <Text data-cs-mask>{address.city}</Text>
            </div>
            <ChangeLocation onClick={() => setEdit(true)}>
              {t('change')}
            </ChangeLocation>
          </SelectedAddress>
        </Wrapper>
      )}
    </MainContainer>
  )
}

PostalCodeSelector.defaultProps = {
  address: {},
  postalCode: '',
  boundaryRef: null,
}

PostalCodeSelector.propTypes = {
  address: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
  }),
  postalCode: PropTypes.string,
  onAddressChange: PropTypes.func.isRequired,
  boundaryRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
}

export default PostalCodeSelector
